import {
  TextInput,
  Textarea,
  Switch,
  Button,
  Stack,
  Group,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconTrash } from '@tabler/icons-react';
import type { JournalEntry, JournalEntryCreate } from '@/types/journal';

interface JournalEntryFormProps {
  entry?: JournalEntry;
  onSubmit: (data: JournalEntryCreate) => void;
  onCancel: () => void;
  onDelete?: (entryId: string) => void;
  isLoading?: boolean;
}

interface FormValues {
  title: string;
  content: string;
  pinned: boolean;
}

export function JournalEntryForm({
  entry,
  onSubmit,
  onCancel,
  onDelete,
  isLoading = false,
}: JournalEntryFormProps) {
  const isEditMode = !!entry;

  const form = useForm<FormValues>({
    initialValues: {
      title: entry?.title ?? '',
      content: entry?.content ?? '',
      pinned: entry?.pinned ?? false,
    },
    validate: {
      content: (value) => (value.trim() ? null : 'Content is required'),
    },
  });

  const handleSubmit = (values: FormValues) => {
    const data: JournalEntryCreate = {
      title: values.title.trim() || undefined,
      content: values.content.trim(),
      pinned: values.pinned,
    };
    onSubmit(data);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <TextInput
          label="Title"
          placeholder="Add a title (optional)"
          {...form.getInputProps('title')}
        />

        <Textarea
          label="Content"
          placeholder="What's on your mind about your tank?"
          required
          minRows={6}
          autosize
          {...form.getInputProps('content')}
        />

        <Switch
          label="Pin to top"
          {...form.getInputProps('pinned', { type: 'checkbox' })}
        />

        <Group justify={isEditMode && onDelete ? 'space-between' : 'flex-end'} mt="md">
          {isEditMode && onDelete && (
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={() => onDelete(entry!.id)}
            >
              Delete
            </Button>
          )}
          <Group gap="sm">
            <Button variant="subtle" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" loading={isLoading} disabled={isLoading}>
              {isEditMode ? 'Update Entry' : 'Add Entry'}
            </Button>
          </Group>
        </Group>
      </Stack>
    </form>
  );
}
